/**
 * /pilot-request — First activation + first paid conversion.
 *
 * Two entry modes:
 *   • Manual:   single product row (kept for one-off users).
 *   • Upload:   CSV/XLSX of procurement rows -> validated -> preview ->
 *               aggregated to one PCF report after payment.
 *
 * Payment flow (both modes): Stripe €29 → mark-paid → download EU PDF.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Rocket, CheckCircle2, Loader2, ArrowLeft, ShieldCheck,
  FileCheck2, Zap, Info, UploadCloud, Download, AlertTriangle, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import { checkoutAPI } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_MB = 20;

export default function PilotRequest() {
  const navigate = useNavigate();
  const polledRef = useRef(false);

  const [mode, setMode] = useState('upload'); // 'upload' | 'manual'
  const [form, setForm] = useState({
    product_name: '',
    material: '',
    quantity: '',
    region: 'EU',
    contact_email: '',
    company_name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [pcfPreview, setPcfPreview] = useState(null);
  const [phase, setPhase] = useState('form'); // form | verifying | success | error

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { session_id, preview, valid_rows, ... }

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim());
  const productOk = form.product_name.trim().length >= 2;

  const manualValid =
    productOk &&
    form.material.trim().length >= 2 &&
    parseFloat(form.quantity) > 0 &&
    emailOk;

  const uploadValid =
    !!uploadResult && uploadResult.valid_rows > 0 && productOk && emailOk;

  // ---- Drag & Drop ---------------------------------------------------------
  const onDrop = useCallback(async (accepted) => {
    const file = accepted?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_FILE_MB} MB.`);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/quick-reports/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setUploadResult(res.data);
      const { total_rows, valid_rows, error_count } = res.data;
      if (valid_rows === 0) {
        toast.error(`No valid rows found. ${error_count} errors — download the template to fix formatting.`);
      } else {
        toast.success(`Parsed ${valid_rows}/${total_rows} rows successfully.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    multiple: false,
  });

  // ---- Submit → Stripe -----------------------------------------------------
  const goCheckout = async (quick_report_id) => {
    const originUrl =
      window.location.origin +
      window.location.pathname +
      `?qr=${encodeURIComponent(quick_report_id)}`;
    const chkRes = await checkoutAPI.createSession('single_report', originUrl);
    if (chkRes?.data?.url) {
      window.location.href = chkRes.data.url;
      return true;
    }
    toast.error('Payment provider did not return a checkout URL.');
    return false;
  };

  const handleManual = async (e) => {
    e.preventDefault();
    if (!manualValid) { toast.error('Please fill product, material, quantity and a valid email.'); return; }
    setSubmitting(true);
    try {
      const qrRes = await axios.post(`${API}/quick-reports`, {
        product_name: form.product_name.trim(),
        material: form.material.trim(),
        quantity: parseFloat(form.quantity),
        region: form.region.trim() || 'EU',
        contact_email: form.contact_email.trim(),
        company_name: form.company_name.trim(),
      });
      const { quick_report_id, pcf_preview } = qrRes.data;
      setPcfPreview(pcf_preview);
      await goCheckout(quick_report_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start report generation.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadValid) {
      toast.error('Upload a valid file, add a product name and a work email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quick-reports/from-upload`, {
        session_id: uploadResult.session_id,
        product_name: form.product_name.trim(),
        contact_email: form.contact_email.trim(),
        company_name: form.company_name.trim(),
        region: form.region.trim() || 'EU',
      });
      const { quick_report_id, pcf_preview } = res.data;
      setPcfPreview(pcf_preview);
      await goCheckout(quick_report_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to prepare report.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Return-from-Stripe polling → download PDF ---------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const qr = params.get('qr');
    if (!sessionId || !qr || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 10;
    const interval = 2500;
    setPhase('verifying');

    const poll = async () => {
      attempts += 1;
      try {
        const r = await checkoutAPI.status(sessionId);
        const paid = r.data?.payment_status === 'paid' || r.data?.status === 'paid';
        if (paid) {
          await axios.post(`${API}/quick-reports/${qr}/mark-paid`, { session_id: sessionId });
          const pdfRes = await axios.get(`${API}/quick-reports/${qr}/download-pdf`, { responseType: 'blob' });
          const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Veridex_EU_Export_Report.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          setPhase('success');
          toast.success('Payment received — your report is downloading.');
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        if (attempts >= maxAttempts) { setPhase('error'); return; }
        setTimeout(poll, interval);
      } catch {
        if (attempts >= maxAttempts) { setPhase('error'); return; }
        setTimeout(poll, interval);
      }
    };
    poll();
  }, []);

  // ------------------- Success / Verifying / Error screens ------------------
  if (phase === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="quick-report-success">
        <Card className="max-w-lg p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">Your report is ready</h1>
          <p className="text-sm text-muted-foreground mb-6">
            A 3-page ISO 14067 EU Export Report has been downloaded. A cryptographic
            verification link is embedded on page 1 so you can share it with
            auditors and customers.
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => navigate('/pricing')} data-testid="quick-report-pricing-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to pricing
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline" data-testid="quick-report-again-btn">
              Generate another report
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (phase === 'verifying') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="quick-report-verifying">
        <Card className="max-w-md p-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary mb-4" />
          <h2 className="text-lg font-heading font-semibold mb-1">Verifying your payment…</h2>
          <p className="text-xs text-muted-foreground">
            Your ISO 14067 EU report will download automatically as soon as the payment clears.
          </p>
        </Card>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="quick-report-error">
        <Card className="max-w-md p-8 text-center border-destructive/40">
          <h2 className="text-lg font-heading font-semibold mb-1">Payment took longer than expected</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Your report is still being processed. Refresh this page in a minute or check your inbox for a download link.
          </p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Card>
      </div>
    );
  }

  // ------------------- Main page ------------------------------------------
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pilot-request-page">
      <header className="border-b border-border bg-card/60 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/"><Logo size={30} variant="full" /></Link>
          <Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="qr-back-pricing">
            &larr; Back to pricing
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-10">
        {/* HERO */}
        <div className="mb-10 text-center max-w-3xl mx-auto" data-testid="qr-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
            <Rocket className="w-3.5 h-3.5" /> Pilot program · Limited seats for EU manufacturers
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
            Generate your first audit-ready Product Carbon Footprint report in minutes
          </h1>
          <p className="mt-4 text-base text-muted-foreground">
            ISO 14067 compliant · EU export ready (CBAM aligned) · QR-verifiable
          </p>
        </div>

        {/* VALUE (3 bullets) */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="qr-value">
          {[
            { i: FileCheck2, t: 'Upload your procurement data', s: 'CSV or Excel (.xlsx) up to 20 MB / 5,000 rows.' },
            { i: Zap,        t: 'We calculate CO₂ emissions', s: 'IEA / DEFRA / IPCC verified factors — deterministic.' },
            { i: ShieldCheck,t: 'Audit-ready PDF after payment', s: 'ISO 14067 aligned, cryptographically signed.' },
          ].map(({ i: Icon, t, s }) => (
            <div key={t} className="rounded-lg border p-4 bg-card/40">
              <Icon className="w-5 h-5 text-primary mb-2" />
              <div className="font-semibold text-sm">{t}</div>
              <div className="text-xs text-muted-foreground mt-1">{s}</div>
            </div>
          ))}
        </div>

        {/* MODE TABS */}
        <div className="flex gap-2 mb-4" data-testid="qr-mode-tabs">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
              mode === 'upload' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground'
            }`}
            data-testid="qr-mode-upload"
          >
            <UploadCloud className="w-4 h-4 inline mr-1.5" /> Upload file
          </button>
          <button
            type="button"
            onClick={() => setMode('manual')}
            className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
              mode === 'manual' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground'
            }`}
            data-testid="qr-mode-manual"
          >
            <FileCheck2 className="w-4 h-4 inline mr-1.5" /> Enter manually
          </button>
        </div>

        {/* INPUT + PAYMENT */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 sm:p-8" data-testid="qr-form-card">
            {mode === 'upload' ? (
              <form className="space-y-5" onSubmit={handleUploadSubmit}>
                <div>
                  <h2 className="font-heading font-bold text-lg mb-1">Procurement data</h2>
                  <p className="text-xs text-muted-foreground mb-4">
                    Upload CSV or Excel. Required columns:&nbsp;
                    <code className="text-[11px] px-1 py-0.5 rounded bg-muted">material_name, quantity, unit, cost, supplier</code>.
                  </p>
                </div>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'
                  }`}
                  data-testid="qr-dropzone"
                >
                  <input {...getInputProps()} data-testid="qr-file-input" />
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Parsing your file…</span>
                    </div>
                  ) : (
                    <>
                      <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium">
                        {isDragActive ? 'Drop your file here…' : 'Drag & drop your file, or click to browse'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        CSV or Excel (.xlsx) · max {MAX_FILE_MB} MB
                      </p>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs">
                  <a
                    href="/templates/procurement-template.csv"
                    download
                    className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    data-testid="qr-download-template"
                  >
                    <Download className="w-3.5 h-3.5" /> Download CSV template
                  </a>
                  {uploadResult && (
                    <span className="text-muted-foreground" data-testid="qr-file-summary">
                      <FileSpreadsheet className="w-3.5 h-3.5 inline mr-1" />
                      {uploadResult.filename} · {uploadResult.valid_rows}/{uploadResult.total_rows} valid rows
                    </span>
                  )}
                </div>

                {uploadResult && uploadResult.valid_rows > 0 && (
                  <div className="rounded-md border bg-card/40" data-testid="qr-preview-table">
                    <div className="px-3 py-2 border-b text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" /> Preview (first 5 rows)
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="text-left px-3 py-1.5">#</th>
                            <th className="text-left px-3 py-1.5">Material</th>
                            <th className="text-right px-3 py-1.5">Qty</th>
                            <th className="text-left px-3 py-1.5">Unit</th>
                            <th className="text-right px-3 py-1.5">Cost</th>
                            <th className="text-left px-3 py-1.5">Supplier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResult.preview.map((row, i) => (
                            <tr key={`${row.material_name}-${row.supplier}-${i}`} className="border-t">
                              <td className="px-3 py-1.5 text-muted-foreground">{i + 1}</td>
                              <td className="px-3 py-1.5 font-medium">{row.material_name}</td>
                              <td className="px-3 py-1.5 text-right">{row.quantity}</td>
                              <td className="px-3 py-1.5">{row.unit}</td>
                              <td className="px-3 py-1.5 text-right">€{row.cost}</td>
                              <td className="px-3 py-1.5">{row.supplier}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-3 py-2 border-t text-xs bg-primary/5 text-primary flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      Estimated aggregate PCF: <strong>{uploadResult.estimated_pcf.toLocaleString()} kg CO₂e</strong>. Full audit PDF is generated after payment.
                    </div>
                  </div>
                )}

                {uploadResult && uploadResult.error_count > 0 && (
                  <div className="rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs" data-testid="qr-errors">
                    <div className="flex items-center gap-1.5 text-amber-900 dark:text-amber-200 font-semibold mb-1">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {uploadResult.error_count} row(s) need review — they will be excluded.
                    </div>
                    <ul className="list-disc pl-5 text-amber-900/90 dark:text-amber-200/90 space-y-0.5">
                      {uploadResult.errors.slice(0, 5).map((e) => (
                        <li key={`err-${e.row}`}>Row {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t">
                  <div className="space-y-1">
                    <Label htmlFor="qru-product">Report / product name *</Label>
                    <Input id="qru-product" required value={form.product_name} onChange={update('product_name')}
                           placeholder="e.g. Q1 2026 Procurement" data-testid="qru-product-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qru-region">Region</Label>
                    <Input id="qru-region" value={form.region} onChange={update('region')}
                           placeholder="EU / DE / FR / …" data-testid="qru-region-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qru-email">Work email *</Label>
                    <Input id="qru-email" type="email" required value={form.contact_email} onChange={update('contact_email')}
                           placeholder="jane@acme.com" data-testid="qru-email-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qru-company">Company</Label>
                    <Input id="qru-company" value={form.company_name} onChange={update('company_name')}
                           placeholder="Acme GmbH" data-testid="qru-company-input" />
                  </div>
                </div>

                <Button type="submit" size="lg"
                        className="w-full bg-primary hover:bg-primary/90 text-base"
                        disabled={!uploadValid || submitting}
                        data-testid="qru-generate-btn">
                  {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Rocket className="w-5 h-5 mr-2" />}
                  {submitting ? 'Preparing checkout…' : 'Generate My Report (€29)'}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Secure checkout by Stripe · PCF is calculated on our servers only after payment.
                </p>
              </form>
            ) : (
              <form className="space-y-4" onSubmit={handleManual}>
                <h2 className="font-heading font-bold text-lg mb-1">Product data</h2>
                <p className="text-xs text-muted-foreground mb-3">
                  Enter a single product. For multi-line procurement, switch to <strong>Upload file</strong>.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="qr-product">Product name *</Label>
                    <Input id="qr-product" required value={form.product_name} onChange={update('product_name')}
                           placeholder="e.g. Hot-Rolled Steel Coil" data-testid="qr-product-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qr-material">Main material *</Label>
                    <Input id="qr-material" required value={form.material} onChange={update('material')}
                           placeholder="Steel / Aluminum / Cement / …" data-testid="qr-material-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qr-qty">Quantity (kg) *</Label>
                    <Input id="qr-qty" required type="number" step="0.01" min="0.01"
                           value={form.quantity} onChange={update('quantity')}
                           placeholder="e.g. 1000" data-testid="qr-qty-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qr-region">Region</Label>
                    <Input id="qr-region" value={form.region} onChange={update('region')}
                           placeholder="EU / DE / FR / …" data-testid="qr-region-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qr-email">Work email *</Label>
                    <Input id="qr-email" type="email" required value={form.contact_email} onChange={update('contact_email')}
                           placeholder="jane@acme.com" data-testid="qr-email-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="qr-company">Company</Label>
                    <Input id="qr-company" value={form.company_name} onChange={update('company_name')}
                           placeholder="Acme GmbH" data-testid="qr-company-input" />
                  </div>
                </div>

                {pcfPreview !== null && (
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="qr-preview">
                    <Info className="w-4 h-4 inline-block mr-1.5 text-primary" />
                    Estimated PCF preview: <strong>{pcfPreview} kg CO₂e</strong>. Full report generated after payment.
                  </div>
                )}

                <Button type="submit" size="lg"
                        className="w-full bg-primary hover:bg-primary/90 text-base"
                        disabled={!manualValid || submitting}
                        data-testid="qr-generate-btn">
                  {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Rocket className="w-5 h-5 mr-2" />}
                  {submitting ? 'Preparing checkout…' : 'Generate My Report (€29)'}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center mt-2">
                  Secure checkout by Stripe · EU VAT inclusive · instant PDF download
                </p>
              </form>
            )}
          </Card>

          {/* PRICING + PAYMENT BLOCK */}
          <div className="space-y-4">
            <Card className="p-5 border-primary/40 bg-primary/5" data-testid="qr-price-card">
              <div className="text-xs text-muted-foreground">One-time report fee</div>
              <div className="text-3xl font-heading font-bold text-primary" data-testid="qr-price">€29</div>
              <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> No subscription required</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Pay only when generating a report</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Instant processing after payment</li>
              </ul>
            </Card>

            <Card className="p-5" data-testid="qr-output-card">
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">What you get</div>
              <ul className="text-xs space-y-1.5">
                {[
                  'ISO 14067 PDF report (3 pages)',
                  'QR verification link',
                  'EU export summary table',
                  'Data quality score (A / B / C)',
                  'Audit trail explanation',
                ].map((s) => (
                  <li key={s} className="flex items-start gap-1.5">
                    <FileCheck2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-5" data-testid="qr-trust-card">
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Trusted methodology</div>
              <ul className="text-xs space-y-1.5 text-muted-foreground">
                <li>• Emission factors: <strong>IEA / DEFRA / IPCC</strong></li>
                <li>• Methodology: <strong>GHG Protocol aligned</strong></li>
                <li>• Cryptographic hash-chain verification</li>
                <li>• Designed for <strong>auditor review</strong></li>
              </ul>
              <div className="mt-3">
                <a
                  href="/methodology.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary underline decoration-dotted"
                  data-testid="qr-methodology-link"
                >
                  Read the full methodology →
                </a>
              </div>
            </Card>
          </div>
        </div>

        {/* PILOT POSITIONING */}
        <div className="mt-10 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="qr-urgency">
          <Zap className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <strong>Pilot program.</strong> Currently open for a limited number of EU manufacturers.
            Report pricing may increase after the pilot window closes.
          </span>
        </div>

        <div className="mt-6 text-center text-sm text-muted-foreground" data-testid="qr-consult-link">
          Need a full 60-90-day CBAM readiness pilot instead?{' '}
          <Link to="/pilot-consultation" className="text-primary underline">Book a consultation →</Link>
        </div>
      </main>
    </div>
  );
}
